import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { DocxClauseEditor, type DocxClauseEditorRef } from './DocxClauseEditor';
import classNames from 'classnames';
import { ReactComponent as CloseIcon } from '../../shared/icons/close-icon.svg';
import { createDocumentTemplate, moveTemplateToFolder, publishTemplateChanges } from '../../api/mutations';
import { updateDocumentTemplateDraft } from '../../utils/api/update-document-offer/update-document';
import { useDocumentState } from '../../contexts/DocumentContext';
import { documentStateFromResponse } from '../../utils/document-wizard-utils/document-state-from-response';
import { getDocumentTemplates, getOrganizationsTemplatesByUser, getTemplateFolders } from '../../api/queries_v2';
import { FolderTreeSelector, TemplateFolder } from '../folder-tree-selector';
import { Button, Form, Dropdown } from 'react-bootstrap';
import { ReactComponent as ChevronDownIcon } from '../../shared/icons/chevron-down.svg';
import styles from './CreateClauseSlideout.module.scss';

interface CreateClauseSlideoutProps {
  show: boolean;
  onHide: () => void;
  teamId: string | null | undefined;
  onClauseCreated?: () => void;
}

const CreateClauseSlideout = ({ show, onHide, teamId, onClauseCreated }: CreateClauseSlideoutProps) => {
  const [clauseTitle, setClauseTitle] = useState('');
  // The clause body lives in the editor, not React state — it is a .docx
  // package, serialized on save. This only tracks whether the author has
  // typed anything, to gate the Create buttons.
  const [hasEdited, setHasEdited] = useState(false);
  const clauseEditorRef = useRef<DocxClauseEditorRef>(null);
  const [loadingAction, setLoadingAction] = useState<'none' | 'creating' | 'creating-publishing'>('none');
  const isLoading = loadingAction !== 'none';
  const [errors, setErrors] = useState<{ title?: string; content?: string }>({});
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const isMountedRef = useRef(true);
  const slideoutRef = useRef(null);
  const apolloClient = useApolloClient();
  const { updateDocumentState } = useDocumentState();

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Proper React lifecycle for CKEditor component
  useEffect(() => {
    if (show) {
      setIsMounted(true);
    } else {
      setIsMounted(false);
    }

    return () => {
      setIsMounted(false);
    };
  }, [show]);

  // Reset form when slideout opens/closes
  useEffect(() => {
    if (show) {
      setClauseTitle('');
      setHasEdited(false);
      setErrors({});
      setSelectedFolderId(null);
    }
  }, [show]);

  // Removed custom editor initialization - using CKEditor React component instead

  // Removed click-outside-to-close to prevent accidental loss of work
  // Users must use Cancel, X, or Create Clause to close

  // Create document template mutation
  const [createClauseTemplate] = useMutation(createDocumentTemplate, {
    refetchQueries: [
      {
        query: getTemplateFolders,
        variables: { teamId, folderType: 'clause' },
      },
      {
        query: getDocumentTemplates,
        variables: { teamId, templateType: 'clause' },
      },
      {
        query: getOrganizationsTemplatesByUser,
        variables: { teamId, templateType: 'clause' },
      },
    ],
  });

  // Move template to folder mutation
  const [moveTemplateToFolderMutation] = useMutation(moveTemplateToFolder);

  // Publish template mutation (for "Create & Publish" flow)
  const [publishChanges] = useMutation(publishTemplateChanges);

  // Get template folders for folder selection
  // Only fetch when slideout is actually shown to reduce unnecessary queries
  const { loading: isFoldersLoading, data: foldersData } = useQuery(getTemplateFolders, {
    variables: { teamId: teamId!, folderType: 'clause' },
    skip: !teamId || !show,
    fetchPolicy: 'network-only',
  });

  const availableFolders: TemplateFolder[] = foldersData?.getTemplateFolders ?? [];

  const validateForm = () => {
    const newErrors: { title?: string; content?: string } = {};

    if (!clauseTitle.trim()) {
      newErrors.title = 'Clause title is required';
    } else if (clauseTitle.trim().length < 2) {
      newErrors.title = 'Clause title must be at least 2 characters';
    }

    // Content is optional - users can create clauses with just titles
    // Content validation only happens if they actually try to save without any content

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (shouldPublish: boolean = false) => {
    if (!validateForm() || !teamId) return;

    setLoadingAction(shouldPublish ? 'creating-publishing' : 'creating');
    try {
      // Step 1: Create the basic template
      const createResult = await createClauseTemplate({
        variables: {
          teamId,
          name: clauseTitle.trim(),
          templateType: 'clause',
          restrictedEditing: false,
        },
      });

      if (createResult.data?.createDocumentTemplate?.id) {
        const templateId = createResult.data.createDocumentTemplate.id;

        // Step 1.5: Move template to folder if one was selected
        if (selectedFolderId && templateId) {
          await moveTemplateToFolderMutation({
            variables: {
              templateId,
              folderId: selectedFolderId,
            },
          });
        }

        // Step 2: serialize the clause as a .docx package.
        //
        // The body IS base64 already — do NOT re-encode it the way the CKEditor
        // path had to (it produced base64 of an HTML string). `clauseBody.ts`
        // classifies a `PK`-prefixed body as DOCX and routes it to
        // `insertDocxFragment`, so the clause inserts as real OOXML with no
        // lossy HTML conversion in between.
        const contentToSave = await clauseEditorRef.current?.getDocxBase64();

        if (!contentToSave) {
          // Refuse to write an empty body over a clause the author just wrote.
          setErrors({ content: 'Could not save the clause content. Please try again.' });
          setLoadingAction('none');
          return;
        }

        const fieldsForUpdate = {
          documentTemplateId: templateId,
          template: {
            body: contentToSave,
            body_storage_format: 'docx',
          },
        };

        // Use draft-aware save function for clause editing
        const response = await updateDocumentTemplateDraft(fieldsForUpdate)(apolloClient);

        // Step 3: Publish if requested (makes clause visible in sidebar immediately)
        // ClauseEditor has no Comments/TrackChanges plugins, so content is inherently clean
        if (shouldPublish && templateId) {
          const publishResult = await publishChanges({
            variables: {
              documentTemplateId: templateId,
              cleanContent: { body: contentToSave },
            },
            refetchQueries: [
              {
                query: getDocumentTemplates,
                variables: { teamId, templateType: 'clause', includeContent: true },
              },
              {
                query: getOrganizationsTemplatesByUser,
                variables: { teamId, templateType: 'clause', includeContent: true },
              },
              {
                query: getTemplateFolders,
                variables: { teamId, folderType: 'clause' },
              },
            ],
            awaitRefetchQueries: true,
          });

          const errors = publishResult?.data?.publishTemplateChanges?.errors || [];
          if (errors.length > 0) {
            throw new Error(`Publish failed: ${errors.join(', ')}`);
          }
        }

        if (response && isMountedRef.current) {
          // Use same update pattern as header/footer
          updateDocumentState(documentStateFromResponse(response, false));

          // Clause lists will be automatically refreshed by mutation refetchQueries
          onClauseCreated?.();
          onHide();
        }
      }
    } catch (error) {
      console.error('Error creating clause template:', error);
      if (isMountedRef.current) {
        setErrors({
          content: shouldPublish
            ? 'Failed to create and publish clause. Please try again.'
            : 'Failed to create clause. Please try again.',
        });
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingAction('none');
      }
    }
  };

  const handleCancel = () => {
    onHide();
  };

  // Check if editor has meaningful content (not just empty HTML tags like <p>&nbsp;</p>)
  // A .docx body can't be tag-stripped like the old HTML one; the editor
  // reports whether the author has actually typed.
  const hasContent = hasEdited;
  const isFormValid = clauseTitle.trim().length >= 2 && hasContent;

  // Don't render anything if not showing
  if (!show) {
    return null;
  }

  // Use React Portal to render slideout outside the sidebar DOM tree
  return ReactDOM.createPortal(
    <>
      {/* Dark overlay backdrop */}
      {show && <div className={styles.slideoutBackdrop} />}

      <div
        className={classNames(
          styles.slideoutWrapper,
          show ? styles.slideoutWrapper__show : styles.slideoutWrapper__collapsed
        )}
      >
        <div ref={slideoutRef} className={styles.slideout}>
          {/* Header */}
          <div className={styles.slideout__header}>
            <div className={styles.slideout__title}>
              <FormattedMessage id="title.create-new-clause" defaultMessage="Create New Clause" />
            </div>
            <div className={styles.slideout__close} onClick={handleCancel}>
              <CloseIcon />
            </div>
          </div>

          {/* Content */}
          <div className={styles.slideout__content}>
            <form className={styles.slideout__form} onSubmit={(e) => e.preventDefault()}>
              {/* Clause Title Field */}
              <div className={styles.slideout__field}>
                <label className={styles.slideout__label} htmlFor="clause-title">
                  <FormattedMessage id="label.title" defaultMessage="Title" />
                  <span style={{ color: '#dc3545' }}> *</span>
                </label>
                <input
                  id="clause-title"
                  type="text"
                  className={styles.slideout__input}
                  value={clauseTitle}
                  onChange={(e) => setClauseTitle(e.target.value)}
                  placeholder="Enter a descriptive title for your clause..."
                  disabled={isLoading}
                  autoFocus
                />
                {errors.title && <div className={styles.slideout__error}>{errors.title}</div>}
              </div>

              {/* Folder Selection Field */}
              <div className={styles.slideout__field}>
                <Form.Label className={styles.slideout__label}>
                  <FormattedMessage id="label.folder" defaultMessage="Folder" />
                </Form.Label>
                {isFoldersLoading ? (
                  <div className={styles.slideout__helpText}>
                    <FormattedMessage id="message.loading-folders" defaultMessage="Loading folders..." />
                  </div>
                ) : (
                  <FolderTreeSelector
                    folders={availableFolders}
                    selectedFolderId={selectedFolderId}
                    onSelectionChange={setSelectedFolderId}
                    disabled={isLoading}
                    context="clauses"
                  />
                )}
              </div>

              {/* Clause Content Field */}
              <div className={classNames(styles.slideout__field, styles.slideout__editorField)}>
                <label className={styles.slideout__label}>
                  <FormattedMessage id="label.content" defaultMessage="Content" />
                  <span style={{ color: '#dc3545' }}> *</span>
                </label>
                <div className={styles.slideout__editorWrapper}>
                  {isMounted && (
                    <DocxClauseEditor
                      ref={clauseEditorRef}
                      onContentChange={setHasEdited}
                      disabled={isLoading}
                    />
                  )}
                </div>
                {errors.content && <div className={styles.slideout__error}>{errors.content}</div>}
              </div>

              {/* Actions */}
              <div className={styles.slideout__actions}>
                <Button variant="secondary" onClick={handleCancel} disabled={isLoading}>
                  <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
                </Button>
                <Dropdown className={styles.slideout__createDropdown} drop="up">
                  <Dropdown.Toggle
                    variant="primary"
                    className={styles.slideout__createToggle}
                    id="create-clause-dropdown"
                    disabled={isLoading || !isFormValid}
                  >
                    {isLoading ? (
                      loadingAction === 'creating-publishing' ? (
                        <FormattedMessage id="button.creating-publishing" defaultMessage="Creating & Publishing..." />
                      ) : (
                        <FormattedMessage id="button.creating" defaultMessage="Creating..." />
                      )
                    ) : (
                      <FormattedMessage id="button.create-clause" defaultMessage="Create Clause" />
                    )}
                    <ChevronDownIcon className={styles.slideout__createChevron} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu alignRight className={styles.slideout__createMenu}>
                    <Dropdown.Item
                      className={styles.slideout__createItem}
                      onClick={() => handleSave(false)}
                      disabled={isLoading}
                    >
                      <FormattedMessage id="button.create-as-draft" defaultMessage="Create as Draft" />
                    </Dropdown.Item>
                    <Dropdown.Item
                      className={styles.slideout__createItem}
                      onClick={() => handleSave(true)}
                      disabled={isLoading}
                    >
                      <FormattedMessage id="button.create-and-publish" defaultMessage="Create & Publish" />
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>,
    document.body // Render to document body to escape sidebar constraints
  );
};

export default CreateClauseSlideout;
